import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react"
import PropTypes from "prop-types"
import { DatePicker, Flex, Checkbox } from "antd"
import { CalendarOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import DaySelector from "./DaySelector"
import styles from "../index.module.css"
import dayjs from "dayjs"
import { startDateValidate, endDateValidate } from "pagesV2/pro/Calendar/utils"

/**
 * Helper function to map API day names to short day keys
 */
const mapApiDaysToKeys = (timeBlocks) => {
  if (!timeBlocks) return []

  const dayMapping = {
    sunday: "sun",
    monday: "mon",
    tuesday: "tue",
    wednesday: "wed",
    thursday: "thu",
    friday: "fri",
    saturday: "sat"
  }

  const days = []
  Object.keys(timeBlocks).forEach((day) => {
    const dayKey = dayMapping[day.toLowerCase()]
    // Check if the day has time blocks (array with items or boolean true)
    if (timeBlocks[day] && (Array.isArray(timeBlocks[day]) ? timeBlocks[day].length > 0 : timeBlocks[day] === true)) {
      days.push(dayKey)
    }
  })

  return days.length > 0 ? days : []
}

/**
 * SeasonForm component for a single season's availability configuration
 */
const SeasonForm = forwardRef(({ seasonNumber, showSeasonTitle, seasonData, onFormChange, isAllYear = false, serverError = null }, ref) => {
  const { t } = useTranslation()

  // Get initial selected days from seasonData
  const getInitialSelectedDays = () => {
    if (seasonData?.time_blocks) {
      const days = mapApiDaysToKeys(seasonData.time_blocks)
      return days.length > 0 ? days : ["mon"]
    }
    return ["mon"]
  }

  // Initialize state from seasonData if available
  const [fromDate, setFromDate] = useState(seasonData?.season_start ? dayjs(seasonData.season_start) : null)
  const [toDate, setToDate] = useState(seasonData?.season_end ? dayjs(seasonData.season_end) : null)
  const [recurringYearly, setRecurringYearly] = useState(seasonData?.recurring_yearly || false)
  const [selectedDay, setSelectedDay] = useState(() => {
    const initialDays = getInitialSelectedDays()
    return initialDays[0] || "mon"
  })
  const [selectedDays, setSelectedDays] = useState(getInitialSelectedDays())
  const [fromDateError, setFromDateError] = useState(null)
  const [toDateError, setToDateError] = useState(null)
  const [daysError, setDaysError] = useState(null)

  // Track if this is the initial mount to avoid calling onFormChange on mount
  const isInitialMount = useRef(true)

  // Update form when seasonData changes
  useEffect(() => {
    if (seasonData) {
      if (seasonData.season_start) {
        setFromDate(dayjs(seasonData.season_start))
      }
      if (seasonData.season_end) {
        setToDate(dayjs(seasonData.season_end))
      }
      if (seasonData.recurring_yearly !== undefined) {
        setRecurringYearly(seasonData.recurring_yearly)
      }
      if (seasonData.time_blocks) {
        const days = mapApiDaysToKeys(seasonData.time_blocks)
        const finalDays = days.length > 0 ? days : ["mon"]
        setSelectedDays(finalDays)
        if (finalDays.length > 0) {
          setSelectedDay(finalDays[0])
        }
      }
    }
    // Clear error when component updates with new data
    setDaysError(null)
  }, [seasonData])

  // Notify parent of changes after state updates (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (onFormChange) {
      onFormChange()
    }
  }, [fromDate, toDate, recurringYearly, selectedDays, onFormChange])

  // Disable dates before today
  const disabledDate = (current) => {
    return current && current < dayjs().startOf("day")
  }

  const handleFromDateChange = async (value) => {
    setFromDate(value)

    // Clear error if date is now filled
    if (value) {
      setFromDateError(null)
    }

    // Validate from date against to date
    if (value && toDate) {
      try {
        await startDateValidate(t, toDate, value)
        setFromDateError(null)
      } catch (error) {
        setFromDateError(error.message)
      }
    }

    // Revalidate to date when from date changes
    if (toDate && value) {
      try {
        await endDateValidate(t, value, toDate)
        setToDateError(null)
      } catch (error) {
        setToDateError(error.message)
      }
    }
  }

  const handleToDateChange = async (value) => {
    setToDate(value)

    // Clear error if date is now filled
    if (value) {
      setToDateError(null)
    }

    // Validate to date against from date
    if (value && fromDate) {
      try {
        await endDateValidate(t, fromDate, value)
        setToDateError(null)
      } catch (error) {
        setToDateError(error.message)
      }
    }

    // Revalidate from date when to date changes
    if (fromDate && value) {
      try {
        await startDateValidate(t, value, fromDate)
        setFromDateError(null)
      } catch (error) {
        setFromDateError(error.message)
      }
    }
  }

  const handleDayClick = (day) => {
    setSelectedDay(day)
  }

  const handleDayToggle = (day, checked) => {
    if (checked) {
      const newDays = [...selectedDays, day]
      setSelectedDays(newDays)
      if (newDays.length > 0) {
        setDaysError(null)
      }
    } else {
      // Prevent deselecting the last day - at least one day must be selected
      if (selectedDays.length <= 1) {
        setDaysError(t("crewMember.availability.validation.atLeastOneDay", { defaultValue: "At least one day must be selected" }))
        return
      }
      setSelectedDays(selectedDays.filter((d) => d !== day))
      setDaysError(null)
    }
  }

  const handleRecurringYearlyChange = (e) => {
    setRecurringYearly(e.target.checked)
  }

  // Expose validate, getData, and hasChanges methods to parent via ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      let isValid = true

      // Only validate from/to dates if not all year
      if (!isAllYear) {
        // Validate from date
        if (!fromDate) {
          setFromDateError(t("crewMember.availability.validation.fromDateRequired", { defaultValue: "From date is required" }))
          isValid = false
        }

        // Validate to date
        if (!toDate) {
          setToDateError(t("crewMember.availability.validation.toDateRequired", { defaultValue: "To date is required" }))
          isValid = false
        }
      }

      // Validate at least one day is selected
      if (selectedDays.length === 0) {
        setDaysError(t("crewMember.availability.validation.atLeastOneDay", { defaultValue: "At least one day must be selected" }))
        isValid = false
      }

      return isValid
    },
    hasChanges: () => {
      // If no initial data, this is a new form - always has changes
      if (!seasonData) {
        return true
      }

      // Check date changes
      const initialFromDate = seasonData.season_start ? dayjs(seasonData.season_start) : null
      const initialToDate = seasonData.season_end ? dayjs(seasonData.season_end) : null

      if (fromDate?.format("YYYY-MM-DD") !== initialFromDate?.format("YYYY-MM-DD")) {
        return true
      }

      if (toDate?.format("YYYY-MM-DD") !== initialToDate?.format("YYYY-MM-DD")) {
        return true
      }

      // Check recurring yearly change
      if (recurringYearly !== (seasonData.recurring_yearly || false)) {
        return true
      }

      // Check selected days change
      const initialDays = mapApiDaysToKeys(seasonData.time_blocks)
      if (selectedDays.length !== initialDays.length) {
        return true
      }

      const sortedSelectedDays = [...selectedDays].sort((a, b) => a.localeCompare(b))
      const sortedInitialDays = [...initialDays].sort((a, b) => a.localeCompare(b))
      for (let i = 0; i < sortedSelectedDays.length; i++) {
        if (sortedSelectedDays[i] !== sortedInitialDays[i]) {
          return true
        }
      }

      return false
    },
    getData: () => {
      // Map short day keys back to API format
      const dayMapping = {
        sun: "sunday",
        mon: "monday",
        tue: "tuesday",
        wed: "wednesday",
        thu: "thursday",
        fri: "friday",
        sat: "saturday"
      }

      // Build time_blocks object
      const timeBlocks = {}
      selectedDays.forEach((dayKey) => {
        const apiDay = dayMapping[dayKey]
        timeBlocks[apiDay] = true // Set to true for "Available" (no specific time blocks yet)
      })

      // Build data based on availability type
      if (isAllYear) {
        return {
          time_blocks: timeBlocks,
          all_year: true
        }
      } else {
        return {
          season_start: fromDate ? fromDate.format("YYYY-MM-DD") : null,
          season_end: toDate ? toDate.format("YYYY-MM-DD") : null,
          time_blocks: timeBlocks,
          all_year: false,
          recurring_yearly: recurringYearly
        }
      }
    }
  }))

  return (
    <Flex vertical gap={24} className={styles.seasonForm}>
      {/* Season Title */}
      {showSeasonTitle && (
        <Text size="xl" fw="600">
          {t("crewMember.availability.season", { number: seasonNumber })}
        </Text>
      )}

      {/* From/To Date Fields - Only show for seasonal availability */}
      {!isAllYear && (
        <>
          <Flex gap={16}>
            <Flex vertical className={styles.dateField}>
              <Text className={styles.fieldLabel}>
                {t("crewMember.availability.fields.from")}
                <span className={styles.required}>*</span>
              </Text>
              <DatePicker
                value={fromDate}
                onChange={handleFromDateChange}
                format="MM/DD/YYYY"
                placeholder="12/11/2026"
                className={styles.datePicker}
                suffixIcon={<CalendarOutlined />}
                disabledDate={disabledDate}
                status={fromDateError ? "error" : ""}
              />
              {fromDateError && <Text className={styles.errorText}>{fromDateError}</Text>}
            </Flex>

            <Flex vertical className={styles.dateField}>
              <Text className={styles.fieldLabel}>
                {t("crewMember.availability.fields.to")}
                <span className={styles.required}>*</span>
              </Text>
              <DatePicker
                value={toDate}
                onChange={handleToDateChange}
                format="MM/DD/YYYY"
                placeholder="12/11/2026"
                className={styles.datePicker}
                suffixIcon={<CalendarOutlined />}
                disabledDate={disabledDate}
                status={toDateError ? "error" : ""}
              />
              {toDateError && <Text className={styles.errorText}>{toDateError}</Text>}
            </Flex>
          </Flex>

          {/* Recurring Yearly Field - Only show for seasonal availability */}
          <Flex align="center" gap={8}>
            <Checkbox checked={recurringYearly} onChange={handleRecurringYearlyChange} />
            <Text>{t("crewMember.availability.fields.recurringYearly")}</Text>
          </Flex>
        </>
      )}

      {/* Server-side validation error */}
      {serverError && <Text className={styles.errorText}>{serverError}</Text>}

      {/* Question Text */}
      <Text fw="500">{t("crewMember.availability.questions.daysAndTimes")}</Text>

      {/* Day Selector */}
      <DaySelector selectedDay={selectedDay} selectedDays={selectedDays} onDayClick={handleDayClick} onDayToggle={handleDayToggle} />
      {daysError && <Text className={styles.errorText}>{daysError}</Text>}

      {/* Time Block Message */}
      <Text className={styles.timeBlockMessage}>{t("crewMember.availability.messages.addDayBlock")}</Text>
    </Flex>
  )
})

SeasonForm.displayName = "SeasonForm"

SeasonForm.propTypes = {
  seasonNumber: PropTypes.number,
  showSeasonTitle: PropTypes.bool,
  seasonData: PropTypes.shape({
    season_start: PropTypes.string,
    season_end: PropTypes.string,
    time_blocks: PropTypes.object,
    all_year: PropTypes.bool,
    recurring_yearly: PropTypes.bool
  }),
  onFormChange: PropTypes.func,
  isAllYear: PropTypes.bool,
  serverError: PropTypes.string
}

SeasonForm.defaultProps = {
  seasonNumber: 1,
  showSeasonTitle: false,
  seasonData: null,
  isAllYear: false,
  serverError: null
}

export default SeasonForm
